export const DEFAULT_ORGANIZATION = "default";

/**
 * Display names for the org dropdown (home menu) only. Keys are always org ids
 * (e.g. "EW-PAAS"); values are display-only (e.g. "EW-HRSN-WS"). All app logic,
 * config keys, and auth use the org id — never the display value.
 *
 * Other org ids referenced in the app (add here if they need a display name):
 * - IONIS (home tabs, profile)
 * - PYX (food orders: "Produce Box")
 * - HA, UHC (referrals)
 */
export const ORGANIZATION_NAMES: Record<string, string> = {
  "BCNC-EMPL-DM": "Diabetes: ASO Groups",
  "BCNC-MCARE-DM": "Diabetes: Medicare",
  "BCNC-IAFP-DM": "Diabetes: Fully Insured",
  "BCNC-COMM-MH": "Maternity: Fully Insured",
  "EW-PAAS": "EW-HRSN-WS"
};

/**
 * Resolves a value that might be an org id or display name to the canonical org id.
 * Used for config lookups (e.g. REACT_APP_TAB_CONFIG) that are keyed by org id.
 */
export function getOrgIdForConfig(orgOrDisplayName: string): string {
  if (!orgOrDisplayName) return orgOrDisplayName;
  const entry = Object.entries(ORGANIZATION_NAMES).find(
    ([, display]) => display === orgOrDisplayName
  );
  return entry ? entry[0] : orgOrDisplayName;
}

/**
 * Maps FHIR eligibility group labels to user-facing copy for specific orgs only.
 * Does not change identifiers sent to APIs—use the raw group string for FHIR and queries.
 *
 * For {@link ORGANIZATION_NAMES} key `BCNC-EMPL-DM` ("Diabetes: ASO Groups"), standalone year
 * `2025` is shown as `2026` so cohort labels stay current in the UI while backend values stay stable.
 *
 * @param orgId - Canonical organization id (e.g. from auth).
 * @param eligibilityGroup - Raw eligibility group string from FHIR.
 * @returns Label safe to show in dropdowns and read-only fields.
 *
 * @example
 * formatEligibilityGroupDisplay("BCNC-EMPL-DM", "2025 ASO Groups") // "2026 ASO Groups"
 * @example
 * formatEligibilityGroupDisplay("BCNC-EMPL-DM", "2024 Employee Group") // "2024 Employee Group"
 */
export function formatEligibilityGroupDisplay(orgId: string, eligibilityGroup: string): string {
  if (orgId !== "BCNC-EMPL-DM") return eligibilityGroup;
  return eligibilityGroup.replace(/\b2025\b/g, "2026");
}

/** Org ids that show the main FOOD tab and use limited profile (Profile + Food Orders only in member view).
 * For member tabs, set REACT_APP_TAB_CONFIG for EW-HRSN-C3 to the same keys array as EW-PAAS (e.g. ["careplan","survey","communication","ai","referral"]). */
export const ORG_IDS_WITH_FOOD_TAB_AND_LIMITED_PROFILE = ["EW-PAAS", "EW-HRSN-C3"] as const;

export function isOrgWithFoodTabAndLimitedProfile(org: string): boolean {
  return (ORG_IDS_WITH_FOOD_TAB_AND_LIMITED_PROFILE as readonly string[]).includes(org);
}